
import {
  defineComponent,
  ref,
  useContext,
  useFetch,
  computed,
} from '@nuxtjs/composition-api';
import { useFetchPage } from '@webplatform/nuxt-cms-js-sdk';
import { prepareBreadcrumbs } from '~/helpers';
import PremiumShopSection from '~/domains/shop/components/PremiumShopSection';

const SHOP_PAGE_TYPE = 'shops';
const PAGE_INFO_TYPE = 'page-info';
const PREMIUM_KEY = 'premium';
const AUTHORISED_KEY = 'authorised';
const OFFICIAL_CENTERS_KEY = 'official_centers';
const PREMIUM_LAYOUT = 'premium_reseller';

export default defineComponent({
  components: { PremiumShopSection },
  setup() {
    const {
      $api: { shopTypesService },
      i18n,
    } = useContext();
    const { route } = useContext();

    const shopTypes = ref();

    const { page, fetchPage } = useFetchPage(route.value.params.slug, {
      type: SHOP_PAGE_TYPE,
      lang: i18n.locale,
    });

    const pageInfo = computed(() =>
      page?.value?.blocks.find(
        (block) => block?.content?.type === PAGE_INFO_TYPE,
      ),
    );
    const sorting = computed(() => pageInfo?.value?.content?.data?.sort);
    const countryId = computed(() => pageInfo?.value?.content?.data?.countryId);

    const sortData = (payload) => {
      return payload.sort((a, b) => {
        return sorting.value.indexOf(a.key) - sorting.value.indexOf(b.key);
      });
    };

    const setShops = ({ shops, name }) => {
      return {
        layout: 'CardSectionShops',
        title: name,
        items: shops.map((item) => ({
          ...item,
          link: {
            src: item?.addresses?.[0]?.url,
          },
          image: { src: item.image, alt: item.name },
        })),
      };
    };

    const setShopsWithAddresses = ({ shops, name }) => {
      return {
        layout: 'PremiumShopSection',
        title: name,
        items: shops?.map((item) => {
          return {
            image: {
              src: item.image,
              alt: item.name,
            },
            addresses: item?.addresses?.map((address) => ({
              title: item.name,
              address: address?.address,
              phones: address?.phone,
              emails: address?.email,
              link: address?.url,
              shopID: item?.id,
            })),
          };
        }),
      };
    };

    const filterDataByLayout = (payload) => {
      return payload.map((item) => {
        if (item.layout.name === PREMIUM_LAYOUT) {
          return setShopsWithAddresses(item);
        }

        return setShops(item);
      });
    };

    const fetchShops = async () => {
      const { data } = await shopTypesService.getAllShopTypes({
        key: [PREMIUM_KEY, AUTHORISED_KEY, OFFICIAL_CENTERS_KEY],
        country_id: countryId.value,
        lang: i18n.locale,
      });

      if (sorting.value) {
        return filterDataByLayout(sortData(data));
      }

      return filterDataByLayout(data);
    };

    useFetch(async () => {
      await fetchPage();
      shopTypes.value = await fetchShops();
    });

    const breadcrumbs = computed(() => [
      ...(page.value?.breadcrumbs || []).map(prepareBreadcrumbs),
      {
        text: page.value?.title,
      },
    ]);

    return {
      page,
      shopTypes,
      breadcrumbs,
    };
  },
  head: {},
});
